import { useWaypoint } from '../WaypointProvider.jsx';
import { formatDate, formatClock } from './bits.jsx';

/* The event endpoint hands relations back as arrays of populated
   records -- trainerId, batchId, venueId, organizerId -- but a record
   written before a relation existed can still carry a bare id or
   nothing at all. Normalised once so the panels below can just map. */
const populated = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.filter((x) => x && typeof x === 'object');
};

/* A row that names another record is the bridge: clicking it, or Enter
   or Space on it, drills in. No "Open" link at the end -- a view list
   has no action column. */
const rowOpen = (label, go) =>
  go
    ? {
        className: 'wp-row--open',
        tabIndex: 0,
        role: 'button',
        'aria-label': label,
        onClick: go,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            go();
          }
        },
      }
    : {};

export default function EventView({ entity, tab }) {
  const { push } = useWaypoint();

  const trainers = populated(entity?.trainerId);
  const batches = populated(entity?.batchId);
  const venue = populated(entity?.venueId)[0];
  const organisers = populated(entity?.organizerId);

  /* Turnout, from GET /mockinterviews/event-stats (merged in by the
     registry). Evaluated first, then those still waiting, so the table
     reads in the order of what is done -- the same order the
     /mock-interviews popup uses. */
  const stats = entity?.mockStats || null;
  const attendedList = Array.isArray(stats?.attendedList) ? stats.attendedList : [];
  const absentList = Array.isArray(stats?.absentList) ? stats.absentList : [];
  const students = [
    ...attendedList.map((s) => ({ ...s, attended: true })),
    ...absentList.map((s) => ({ ...s, attended: false })),
  ];

  if (tab === 'trainers') {
    if (trainers.length === 0) {
      return <p className="lp-muted">No trainer is assigned to this event.</p>;
    }

    return (
      <div className="lp-dialog-table">
        <table>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Trainer</th>
              <th>Email</th>
              <th>Specialization</th>
            </tr>
          </thead>
          <tbody>
            {trainers.map((t, i) => (
              <tr
                key={t?.id || `${t?.trainerName}-${i}`}
                {...rowOpen(
                  `Open ${t?.trainerName || 'this trainer'}`,
                  t?.id ? () => push('trainer', t.id) : null,
                )}
              >
                <td className="lp-num">{i + 1}</td>
                <td className="lp-name">{t?.trainerName || 'Unnamed trainer'}</td>
                <td>{t?.trainerEmail || '—'}</td>
                <td>{t?.specialization || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tab === 'batches') {
    if (batches.length === 0) {
      return <p className="lp-muted">This event is not tied to a batch.</p>;
    }

    return (
      <div className="lp-dialog-table">
        <table>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Batch</th>
              <th>Mode</th>
              <th>Runs</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b, i) => (
              <tr
                key={b?.id || `${b?.name}-${i}`}
                {...rowOpen(
                  `Open ${b?.name || 'this batch'}`,
                  b?.id ? () => push('batch', b.id) : null,
                )}
              >
                <td className="lp-num">{i + 1}</td>
                <td className="lp-name">{b?.name || 'Unnamed batch'}</td>
                <td>{b?.mode || '—'}</td>
                <td>
                  {formatDate(b?.startTime)} – {formatDate(b?.endTime)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tab === 'students') {
    if (students.length === 0) {
      return (
        <p className="lp-muted">
          {stats
            ? 'No students are enrolled in this event’s batches yet.'
            : 'No turnout was recorded for this event.'}
        </p>
      );
    }

    return (
      <div className="lp-dialog-table">
        <table>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Student</th>
              <th>Attended</th>
              <th>Score</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, i) => {
              /* Attendance and result are two different questions. A
                 student can turn up and not be evaluated yet, so "Not
                 evaluated" is a result, not an absence. */
              const evaluated = s.attended && s.result;
              const qualified =
                String(s.result || '').trim().toLowerCase() === 'qualified';
              return (
                <tr
                  key={s.studentId || `${s.name}-${i}`}
                  {...rowOpen(
                    `Open ${s.name || 'this student'}`,
                    s.studentId ? () => push('student', s.studentId) : null,
                  )}
                >
                  <td className="lp-num">{i + 1}</td>
                  <td className="lp-name">{s.name || 'Unnamed student'}</td>
                  <td>
                    <span
                      className={`pg-pill ${
                        s.attended ? 'pg-pill--good' : 'pg-pill--bad'
                      }`}
                    >
                      {s.attended ? 'Attended' : 'Absent'}
                    </span>
                  </td>
                  <td>
                    {s.scorePercent === null || s.scorePercent === undefined
                      ? '—'
                      : `${s.scorePercent}%`}
                  </td>
                  <td>
                    {evaluated ? (
                      <span
                        className={`pg-pill ${
                          qualified ? 'pg-pill--good' : 'pg-pill--bad'
                        }`}
                      >
                        {qualified ? 'Qualified' : s.result || 'Unqualified'}
                      </span>
                    ) : (
                      <span className="lp-muted">Not evaluated</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <dl className="quiz-facts">
        <div>
          <dt>Date</dt>
          <dd>{formatDate(entity?.date)}</dd>
        </div>
        <div>
          <dt>Time</dt>
          <dd>{entity?.time ? formatClock(entity.time) : 'All day'}</dd>
        </div>
        <div>
          <dt>Category</dt>
          <dd>{entity?.category || '—'}</dd>
        </div>
        <div>
          <dt>Priority</dt>
          <dd>{entity?.priority && entity.priority !== 'Not set' ? entity.priority : '—'}</dd>
        </div>
        <div>
          <dt>Venue</dt>
          {/* venueId is a classroom when one is set, and free text when
              the event is somewhere that is not a room the LMS knows. */}
          <dd>
            {venue?.classroomNo || entity?.classroomNumber || entity?.offlineAddress || '—'}
          </dd>
        </div>
        <div>
          <dt>Organiser</dt>
          <dd>
            {entity?.organizerName ||
              organisers.map((o) => o?.name || o?.email).filter(Boolean).join(', ') ||
              '—'}
          </dd>
        </div>
        <div>
          <dt>Contact</dt>
          <dd>
            {[entity?.organizerEmail, entity?.organizerPhone].filter(Boolean).join(' · ') ||
              '—'}
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{entity?.status || 'Active'}</dd>
        </div>
      </dl>

      {entity?.onlineLink && (
        <>
          <h5 className="pgm-section-title">Meeting</h5>
          <p className="ov-meeting">
            {/* An external link, so a real anchor rather than a
                WaypointLink -- this one does leave the app. */}
            <a href={entity.onlineLink} target="_blank" rel="noopener noreferrer">
              {entity.onlineLink}
            </a>
          </p>
        </>
      )}

      {entity?.agenda && (
        <>
          <h5 className="pgm-section-title">Agenda</h5>
          <p className="lp-prose">{entity.agenda}</p>
        </>
      )}

      {entity?.description && (
        <>
          <h5 className="pgm-section-title">About</h5>
          <p className="lp-prose">{entity.description}</p>
        </>
      )}

      {entity?.highlights && (
        <>
          <h5 className="pgm-section-title">Highlights</h5>
          <p className="lp-prose">{entity.highlights}</p>
        </>
      )}
    </>
  );
}
